import type { CanvasNode, Connection, NodeData } from '../types';
import { findEmptyPosition } from './canvasUtils';
import { isWorkspaceEditNode } from './workspaceEdit';

/**
 * Copying and pasting nodes, within a workspace or into another one (also in another
 * tab, through the system clipboard). A copy carries the selected nodes and the
 * connections between them; a paste gives every node a new id, wires the connections to
 * the new ids, and places the group where the user is looking, with its layout kept.
 */

export interface NodeClipboard {
    app: 'infinite-canvas';
    version: 2;
    /** The workspace the nodes were copied from, so a paste there can mark its copies. */
    sourceWorkspace?: string;
    /** When the copy was made, so the newer of two copies (system clipboard, this tab) is pasted. */
    copiedAt?: number;
    nodes: CanvasNode[];
    connections: Connection[];
}

const FALLBACK_SIZE = { width: 320, height: 200 };

/** The selected nodes and the connections between them. The workspace's Edit page node never travels. */
export const buildNodeClipboard = (
    nodes: CanvasNode[],
    connections: Connection[],
    selectedIds: string[],
    sourceWorkspace?: string,
): NodeClipboard | null => {
    const wanted = new Set(selectedIds);
    const copied = nodes.filter(node => wanted.has(node.id) && !isWorkspaceEditNode(node));
    if (copied.length === 0) return null;
    const copiedIds = new Set(copied.map(node => node.id));
    return {
        app: 'infinite-canvas',
        version: 2,
        ...(sourceWorkspace ? { sourceWorkspace } : {}),
        copiedAt: Date.now(),
        nodes: copied,
        connections: connections.filter(c => copiedIds.has(c.sourceId) && copiedIds.has(c.targetId)),
    };
};

const isNode = (value: unknown): value is CanvasNode =>
    typeof value === 'object' && value !== null
    && typeof (value as CanvasNode).id === 'string'
    && typeof (value as CanvasNode).type === 'string'
    && typeof (value as CanvasNode).position?.x === 'number'
    && typeof (value as CanvasNode).position?.y === 'number';

const isConnection = (value: unknown): value is Connection =>
    typeof value === 'object' && value !== null
    && typeof (value as Connection).sourceId === 'string' && typeof (value as Connection).targetId === 'string';

/** A clipboard text written by this app (this version or the older one-node form), or null for anything else. */
export const parseNodeClipboard = (text: string | null | undefined): NodeClipboard | null => {
    if (!text || text[0] !== '{') return null;
    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        return null;
    }
    const data = parsed as { app?: unknown; node?: unknown; nodes?: unknown; connections?: unknown; sourceWorkspace?: unknown; copiedAt?: unknown };
    if (data?.app !== 'infinite-canvas') return null;
    const nodes = Array.isArray(data.nodes) ? data.nodes.filter(isNode) : isNode(data.node) ? [data.node] : [];
    if (nodes.length === 0) return null;
    return {
        app: 'infinite-canvas',
        version: 2,
        ...(typeof data.sourceWorkspace === 'string' ? { sourceWorkspace: data.sourceWorkspace } : {}),
        ...(typeof data.copiedAt === 'number' ? { copiedAt: data.copiedAt } : {}),
        nodes,
        connections: Array.isArray(data.connections) ? data.connections.filter(isConnection) : [],
    };
};

/** A pasted node starts fresh: a generation that was running belongs to the original, not the copy. */
const copyData = (data: NodeData, markAsCopy: boolean): NodeData => {
    const wasGenerating = data.generationStatus === 'generating';
    const next: NodeData = {
        ...data,
        timestamp: Date.now(),
        jobId: undefined,
        ...(wasGenerating ? { generationStatus: 'idle' as const, progress: 0 } : {}),
    };
    if (wasGenerating && next.videoConfig) {
        const { generationOwnerId: _owner, ...videoConfig } = next.videoConfig;
        next.videoConfig = videoConfig;
    }
    // A storyboard's frames generate in the original too
    const boxes = next.storyboardConfig?.boxes;
    if (next.storyboardConfig && Array.isArray(boxes) && boxes.some(box => box.status === 'generating')) {
        next.storyboardConfig = { ...next.storyboardConfig, boxes: boxes.map(box => (box.status === 'generating' ? { ...box, status: 'idle' as const } : box)) };
    }
    if (markAsCopy) next.title = data.title ? `${data.title} (Copy)` : 'Copy';
    return next;
};

/**
 * The clipboard's nodes and connections as new canvas items: new ids, connections wired
 * to them, and the group placed around `center` (canvas coordinates) on free space, with
 * the nodes' positions relative to each other kept.
 */
export const placePastedNodes = (
    clipboard: NodeClipboard,
    options: {
        existingNodes: CanvasNode[];
        center: { x: number; y: number };
        /** Pasting into the workspace the nodes came from: their titles get " (Copy)". */
        markAsCopy: boolean;
        makeId: () => string;
    },
): { nodes: CanvasNode[]; connections: Connection[] } => {
    const sizeOf = (node: CanvasNode) => node.size ?? FALLBACK_SIZE;
    const left = Math.min(...clipboard.nodes.map(node => node.position.x));
    const top = Math.min(...clipboard.nodes.map(node => node.position.y));
    const right = Math.max(...clipboard.nodes.map(node => node.position.x + sizeOf(node).width));
    const bottom = Math.max(...clipboard.nodes.map(node => node.position.y + sizeOf(node).height));
    const width = right - left;
    const height = bottom - top;
    const spot = findEmptyPosition(options.center.x - width / 2, options.center.y - height / 2, width, height, options.existingNodes);
    const dx = spot.x - left;
    const dy = spot.y - top;

    const newIds = new Map(clipboard.nodes.map(node => [node.id, options.makeId()]));
    const nodes = clipboard.nodes.map(node => ({
        ...node,
        id: newIds.get(node.id)!,
        position: { x: node.position.x + dx, y: node.position.y + dy },
        data: copyData(node.data, options.markAsCopy),
    }));
    const connections = clipboard.connections
        .filter(c => newIds.has(c.sourceId) && newIds.has(c.targetId))
        .map(c => ({ id: options.makeId(), sourceId: newIds.get(c.sourceId)!, targetId: newIds.get(c.targetId)! }));
    return { nodes, connections };
};

/** Above this size the system clipboard is skipped (copies stay in this tab only): writing it would stall the page. */
export const SYSTEM_CLIPBOARD_LIMIT = 20 * 1024 * 1024;

/**
 * Which nodes are selected after a press on `id`. With Shift, Ctrl or Cmd the node is
 * added or taken out. A plain press keeps a multi-selection the node belongs to (so the
 * group can be dragged) and otherwise selects only that node.
 */
export const nextSelection = (current: string[], id: string, additive: boolean): string[] => {
    if (additive) return current.includes(id) ? current.filter(x => x !== id) : [...current, id];
    if (current.length > 1 && current.includes(id)) return current;
    return [id];
};
